/**
 * State + thunks for the Org Settings → Billing tab.
 *
 * Pattern A (hybrid): we host the plan summary + change/cancel/resume in
 * the page; Stripe Customer Portal handles invoices + payment-method
 * updates + receipts. Saves us from rebuilding 40h of Stripe-Portal UI.
 *
 * The subscription itself lives on currentUser.privateData.orgSubscriptions
 * (already loaded by fetchCurrentUser); this duck only tracks the
 * in-progress / error state of the four mutation actions.
 */

import { features } from '../../config/features';
import { fetchCurrentUser } from '../../ducks/user.duck';
import { fetchOrganizationProfiles } from '../../ducks/organizations.duck';
import {
  cancelSubscription,
  changeSubscriptionTier,
  createCustomerPortalSession,
  createSubscriptionCheckout,
  resumeSubscription,
} from '../../api/subscriptions';
import { storableError } from '../../util/errors';

// ================ Action types ================ //

export const CANCEL_REQUEST = 'app/OrgSettingsBillingPage/CANCEL_REQUEST';
export const CANCEL_SUCCESS = 'app/OrgSettingsBillingPage/CANCEL_SUCCESS';
export const CANCEL_ERROR = 'app/OrgSettingsBillingPage/CANCEL_ERROR';

export const RESUME_REQUEST = 'app/OrgSettingsBillingPage/RESUME_REQUEST';
export const RESUME_SUCCESS = 'app/OrgSettingsBillingPage/RESUME_SUCCESS';
export const RESUME_ERROR = 'app/OrgSettingsBillingPage/RESUME_ERROR';

export const CHANGE_PLAN_REQUEST = 'app/OrgSettingsBillingPage/CHANGE_PLAN_REQUEST';
export const CHANGE_PLAN_SUCCESS = 'app/OrgSettingsBillingPage/CHANGE_PLAN_SUCCESS';
export const CHANGE_PLAN_ERROR = 'app/OrgSettingsBillingPage/CHANGE_PLAN_ERROR';

export const OPEN_PORTAL_REQUEST = 'app/OrgSettingsBillingPage/OPEN_PORTAL_REQUEST';
export const OPEN_PORTAL_SUCCESS = 'app/OrgSettingsBillingPage/OPEN_PORTAL_SUCCESS';
export const OPEN_PORTAL_ERROR = 'app/OrgSettingsBillingPage/OPEN_PORTAL_ERROR';

// ================ Reducer ================ //

const initialState = {
  cancelInProgress: false,
  cancelError: null,
  resumeInProgress: false,
  resumeError: null,
  changePlanInProgress: false,
  changePlanError: null,
  openPortalInProgress: false,
  openPortalError: null,
};

export default function reducer(state = initialState, action = {}) {
  const { type, payload } = action;
  switch (type) {
    case CANCEL_REQUEST:
      return { ...state, cancelInProgress: true, cancelError: null };
    case CANCEL_SUCCESS:
      return { ...state, cancelInProgress: false };
    case CANCEL_ERROR:
      return { ...state, cancelInProgress: false, cancelError: payload };

    case RESUME_REQUEST:
      return { ...state, resumeInProgress: true, resumeError: null };
    case RESUME_SUCCESS:
      return { ...state, resumeInProgress: false };
    case RESUME_ERROR:
      return { ...state, resumeInProgress: false, resumeError: payload };

    case CHANGE_PLAN_REQUEST:
      return { ...state, changePlanInProgress: true, changePlanError: null };
    case CHANGE_PLAN_SUCCESS:
      return { ...state, changePlanInProgress: false };
    case CHANGE_PLAN_ERROR:
      return { ...state, changePlanInProgress: false, changePlanError: payload };

    case OPEN_PORTAL_REQUEST:
      return { ...state, openPortalInProgress: true, openPortalError: null };
    case OPEN_PORTAL_SUCCESS:
      return { ...state, openPortalInProgress: false };
    case OPEN_PORTAL_ERROR:
      return { ...state, openPortalInProgress: false, openPortalError: payload };

    default:
      return state;
  }
}

// ================ Thunks ================ //

/**
 * Cancel at period end. The Stripe webhook will eventually update
 * privateData.orgSubscriptions[orgId].cancelAtPeriodEnd, but we re-fetch
 * the user immediately so the UI flips to "Resume" without waiting for
 * the webhook.
 */
export const cancelOrgSubscription = orgId => async dispatch => {
  dispatch({ type: CANCEL_REQUEST });
  try {
    await cancelSubscription(orgId, false);
    await dispatch(fetchCurrentUser());
    dispatch({ type: CANCEL_SUCCESS });
  } catch (e) {
    dispatch({ type: CANCEL_ERROR, payload: storableError(e) });
    throw e;
  }
};

export const resumeOrgSubscription = orgId => async dispatch => {
  dispatch({ type: RESUME_REQUEST });
  try {
    await resumeSubscription(orgId);
    await dispatch(fetchCurrentUser());
    dispatch({ type: RESUME_SUCCESS });
  } catch (e) {
    dispatch({ type: RESUME_ERROR, payload: storableError(e) });
    throw e;
  }
};

export const changeOrgPlan = (orgId, targetTier, interval) => async dispatch => {
  dispatch({ type: CHANGE_PLAN_REQUEST });
  try {
    const response = await changeSubscriptionTier(orgId, targetTier, interval);
    await dispatch(fetchCurrentUser());
    dispatch({ type: CHANGE_PLAN_SUCCESS });
    return response;
  } catch (e) {
    dispatch({ type: CHANGE_PLAN_ERROR, payload: storableError(e) });
    throw e;
  }
};

/**
 * Freemium → paid: create a Stripe Checkout session and redirect. The
 * subscription is created on Stripe's hosted page; the webhook +
 * inline write in createSubscriptionCheckout's success handler then
 * populate privateData.orgSubscriptions before the user lands back on
 * /settings/billing?upgrade=success.
 */
export const startOrgCheckout = (orgId, targetTier, interval) => async dispatch => {
  dispatch({ type: CHANGE_PLAN_REQUEST });
  try {
    const response = await createSubscriptionCheckout(orgId, targetTier, interval);
    const url = response?.checkoutUrl;
    if (!url) throw new Error('Checkout URL missing in response');
    dispatch({ type: CHANGE_PLAN_SUCCESS });
    return url;
  } catch (e) {
    dispatch({ type: CHANGE_PLAN_ERROR, payload: storableError(e) });
    throw e;
  }
};

/**
 * Open the Stripe Customer Portal for the org. Returns the portal URL so
 * the component can `window.location.href = url`. The portal handles
 * invoices, receipts, payment-method updates, billing address, tax IDs.
 */
export const openOrgCustomerPortal = (orgId, returnPath) => async dispatch => {
  dispatch({ type: OPEN_PORTAL_REQUEST });
  try {
    const response = await createCustomerPortalSession(orgId, returnPath);
    dispatch({ type: OPEN_PORTAL_SUCCESS });
    return response?.portalUrl || null;
  } catch (e) {
    dispatch({ type: OPEN_PORTAL_ERROR, payload: storableError(e) });
    throw e;
  }
};

// ================ loadData ================ //

export const loadData = () => async dispatch => {
  // Plans off (plan 6p): the page is not-found, so it asks for nothing
  if (!features.paidPlans) {
    return null;
  }
  // Subscription lives on currentUser.privateData; make sure it's fresh
  // before rendering. Org profile data is incidentally useful for the
  // page header.
  await dispatch(fetchCurrentUser());
  await dispatch(fetchOrganizationProfiles());
  return null;
};
